// Transaction Service for fetching and managing transactions

// Helper function to get admin token
const getAdminHeaders = () => {
  const adminToken =
    localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
  console.log("ADMIN TOKEN:", adminToken);
  const headers = {
    "Content-Type": "application/json",
  };
  if (adminToken) {
    headers["Authorization"] = `Bearer ${adminToken}`;
  }
  return headers;
};

// Helper function to make API requests with retry logic across multiple bases
const apiRequestWithRetry = async (endpoint, options = {}) => {
  const defaultOptions = {
    headers: getAdminHeaders(),
  };

  const primaryBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
  const apiBases = [primaryBase];

  let lastErr = null;

  for (const base of apiBases) {
    const url = `${base}${endpoint}`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);

      const response = await fetch(url, {
        ...defaultOptions,
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const text = await response.text();

      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { message: text };
      }

      if (response.ok) {
        return data;
      }

      // Server responded with an HTTP status code (do not fallback to production)
      if (response.status === 401) {
        console.error("🔒 401 Unauthorized encountered on API endpoint:", url);
        throw new Error("Unauthorized (401) - Session expired or invalid admin token. Please re-login.");
      }

      if (response.status === 404) {
        return null; // Return null gracefully for missing user/vendor lookups
      }

      throw new Error(data.message || data.error || `HTTP ${response.status}`);
    } catch (error) {
      if (error.message && error.message.includes("Unauthorized")) {
        throw error;
      }
      console.warn(`⚠️ API Call Notice (${url}):`, error.message);
      lastErr = error;
    }
  }

  throw lastErr || new Error("API request failed");
};

export const transactionService = {
  // Fetch all transactions (admin only)
  async getAllTransactions(limit = 100) {
    try {
      const data = await apiRequestWithRetry(
        `/api/transactions/admin/all?limit=${limit}`
      );
      return data.transactions || [];
    } catch (error) {
      console.error("Error fetching transactions:", error);
      throw error;
    }
  },

  // Delete transaction (admin only)
  async deleteTransaction(transactionId) {
    try {
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const data = await apiRequestWithRetry(
        `/api/transactions/admin/${transactionId}`,   // ← CORRECT
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
          },
        }
      );
      return data;
    } catch (error) {
      console.error("Error deleting transaction:", error);
      throw error;
    }
  },

  // In-memory tax rates cache
  _taxRatesCache: null,
  _taxRatesCacheTimestamp: 0,

  // Get current tax rates (Single collection configuration)
  async getTaxRates() {
    const now = Date.now();
    if (this._taxRatesCache && (now - this._taxRatesCacheTimestamp < 60000)) {
      return this._taxRatesCache;
    }

    try {
      const data = await apiRequestWithRetry("/api/admins/tax-rates");
      const rates = data.taxRates || { gst: 0.18, serviceTax: 0, platformFee: 0.05 };
      this._taxRatesCache = rates;
      this._taxRatesCacheTimestamp = now;
      return rates;
    } catch (error) {
      try {
        const fallbackData = await apiRequestWithRetry("/api/admin/config/tax-rates");
        const rates = fallbackData.taxRates || fallbackData.config?.taxRates || { gst: 0.18, serviceTax: 0, platformFee: 0.05 };
        this._taxRatesCache = rates;
        this._taxRatesCacheTimestamp = now;
        return rates;
      } catch (err) {
        console.error("Error fetching tax rates:", err);
        throw err;
      }
    }
  },

  // Update tax rates (Single collection configuration)
  async updateTaxRates(taxRates) {
    this._taxRatesCache = null;
    this._taxRatesCacheTimestamp = 0;
    try {
      const data = await apiRequestWithRetry("/api/admins/tax-rates", {
        method: "PUT",
        body: JSON.stringify(taxRates),
      });
      const updated = data.taxRates || data.config?.taxRates || taxRates;
      this._taxRatesCache = updated;
      this._taxRatesCacheTimestamp = Date.now();
      return updated;
    } catch (error) {
      const fallbackData = await apiRequestWithRetry("/api/admin/config/tax-rates", {
        method: "PUT",
        body: JSON.stringify(taxRates),
      });
      const updated = fallbackData.taxRates || fallbackData.config?.taxRates || taxRates;
      this._taxRatesCache = updated;
      this._taxRatesCacheTimestamp = Date.now();
      return updated;
    }
  },

  // Fetch booking data by ID (admin only)
  async getBookingById(bookingId) {
    try {
      // Try the admin booking endpoint first
      let data;
      try {
        data = await apiRequestWithRetry(`/api/bookings/admin/${bookingId}`);
      } catch (error) {
        // Fallback to regular booking endpoint
        data = await apiRequestWithRetry(`/api/bookings/${bookingId}`);
      }

      return data.booking || data;
    } catch (error) {
      console.error("Error fetching booking data:", error);
      throw error;
    }
  },

  // Fetch user data by ID (admin only)
  async getUserById(userId) {
    try {
      const data = await apiRequestWithRetry(`/api/users/${userId}`);
      return data.user || data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  },

  // Fetch vendor data by ID (admin only with fallback)
  async getVendorById(vendorId) {
    try {
      const data = await apiRequestWithRetry(`/api/vendors/${vendorId}`);
      return data.vendor || data;
    } catch (error) {
      try {
        const fallbackData = await apiRequestWithRetry(`/api/vendors/info/${vendorId}`);
        return fallbackData.vendor || fallbackData;
      } catch (err) {
        console.warn(`Vendor ${vendorId} not found:`, err.message);
        return null;
      }
    }
  },
};

export default transactionService;
